import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'data-table-page-size';
const DEFAULT_PAGE_SIZE = 10;

/**
 * Hook for managing table page size with localStorage persistence
 * All tables share the same page size preference
 */
export const useTablePageSize = (initialPageSize?: number) => {
  // Get initial page size from localStorage or use default
  const getStoredPageSize = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedSize = parseInt(stored, 10);
        if (parsedSize > 0) {
          return parsedSize;
        }
      }
    } catch {
      // Best-effort: localStorage may be unavailable.
    }
    return initialPageSize || DEFAULT_PAGE_SIZE;
  }, [initialPageSize]);

  const [pageSize, setPageSize] = useState<number>(() => getStoredPageSize());

  // Save page size to localStorage whenever it changes
  const updatePageSize = useCallback((newPageSize: number) => {
    if (newPageSize > 0) {
      setPageSize(prevSize => {
        if (prevSize === newPageSize) return prevSize; // Prevent unnecessary updates

        try {
          localStorage.setItem(STORAGE_KEY, newPageSize.toString());
        } catch {
          // Best-effort: ignore localStorage write failures.
        }

        return newPageSize;
      });
    }
  }, []);

  // Listen for localStorage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const newSize = parseInt(e.newValue, 10);
        if (newSize > 0) {
          setPageSize(newSize);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    pageSize,
    setPageSize: updatePageSize,
  };
};
